import { useEffect, useRef, useState } from 'react';
import { api, errorText } from '../api';
import { Dialog } from '../components/Dialog';
import { canShareFiles } from '../components/ShareFile';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { useSchmal } from './tarifHinweis';
import type { RtItem } from './types';
import './versand.css';

// Billtoro: gleich nach dem Schreiben „Rechnung verschicken“ („Rechnungen schreiben so einfach wie eine Nachricht“).
// Drei gleich große Wege: per E-Mail (VersandDialog.tsx), Teilen über das Teilen-Menü des Geräts (Messenger,
// Mail-Programm, Dateien; kann der Browser keine Dateien teilen: die PDF herunterladen) und per Post, wenn der Tarif
// den Brief enthält. Am Handy ist Teilen der Hauptknopf, am Rechner die E-Mail. Darunter: PDF ansehen oder später.
// onGeteilt: geteilt oder heruntergeladen (die Rechnungsliste zählt es als verschickt)

export interface VerschickenBeleg {
  id: string;
  // „Rechnung RE-2026-0001“ (Dateiname beim Teilen) und die Art („Rechnung“, „Abschlagsrechnung“ …) für den Titel
  titel: string;
  art: string;
}

const pdfUrl = (id: string) => `/api/rechnungstool/rechnung/${id}/pdf`;
const dateiName = (titel: string) => `${titel.trim().replace(/[^\p{L}\p{N}.-]+/gu, '-')}.pdf`;

export function VerschickenDialog({
  beleg,
  item,
  onClose,
  onMail,
  onPost,
  onAnsehen,
  onGeteilt,
}: {
  beleg: VerschickenBeleg | null;
  // die Rechnung aus der Liste (Kunde, Betrag), fehlt sie: nur der Titel
  item?: RtItem | null;
  onClose(): void;
  onMail(): void;
  // nur, wenn der Brief im Tarif ist
  onPost?(): void;
  onAnsehen(): void;
  onGeteilt?(id: string, weg: 'teilen' | 'download'): void;
}) {
  const toast = useToast();
  const schmal = useSchmal();
  const teilen = canShareFiles();
  const datei = useRef<{ id: string; p: Promise<File> } | null>(null);
  const [busy, setBusy] = useState(false);

  // Die PDF schon beim Öffnen laden: Safari öffnet das Teilen-Menü nur direkt nach dem Tippen
  const laden = (b: VerschickenBeleg) => {
    if (datei.current?.id !== b.id) {
      const p = api.file(pdfUrl(b.id)).then((blob) => new File([blob], dateiName(b.titel), { type: blob.type || 'application/pdf' }));
      p.catch(() => {
        if (datei.current?.p === p) datei.current = null;
      });
      datei.current = { id: b.id, p };
    }
    return datei.current.p;
  };
  useEffect(() => {
    if (beleg && teilen) void laden(beleg).catch(() => {});
    // nur beim Öffnen für eine andere Rechnung
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beleg?.id, teilen]);

  async function share() {
    if (!beleg) return;
    setBusy(true);
    try {
      await navigator.share({ files: [await laden(beleg)] });
      onGeteilt?.(beleg.id, 'teilen');
      onClose();
    } catch (err) {
      // abgebrochen: nichts zu melden
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        toast('Die Datei ist bereit. Bitte noch einmal auf „Teilen“ tippen.');
        return;
      }
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const mail = (
    <button key="mail" type="button" className={`btn block${schmal ? '' : ' primary'}`} onClick={onMail}>
      Per E-Mail
    </button>
  );
  const geraet = teilen ? (
    <button
      key="teilen"
      type="button"
      className={`btn block${schmal ? ' primary' : ''}`}
      disabled={busy}
      onPointerDown={() => beleg && void laden(beleg).catch(() => {})}
      onClick={() => void share()}
    >
      Teilen (z. B. Messenger)
    </button>
  ) : (
    <a
      key="teilen"
      className={`btn block${schmal ? ' primary' : ''}`}
      href={beleg ? `${pdfUrl(beleg.id)}?download=1` : undefined}
      onClick={() => beleg && onGeteilt?.(beleg.id, 'download')}
    >
      PDF herunterladen
    </a>
  );
  const post = onPost && (
    <button key="post" type="button" className="btn block" onClick={onPost}>
      Per Post
    </button>
  );

  return (
    <Dialog open={!!beleg} onClose={onClose} title={beleg ? `${beleg.art} verschicken` : ''} className="vt-dlg">
      {beleg && (
        <>
          <p className="vt-wer">
            {item ? (
              <>
                <b className="num">{item.number}</b> an {item.customer} · <span className="num">{eurC(item.amount, item.waehrung)}</span>
              </>
            ) : (
              beleg.titel
            )}
          </p>
          <div className="vt-wege">{schmal ? [geraet, mail, post] : [mail, geraet, post]}</div>
          <p className="rt-hint vt-hint">
            {teilen
              ? 'Teilen öffnet das Menü Ihres Geräts: Messenger, Mail-Programm oder Dateien.'
              : 'Die PDF können Sie danach in jeder App verschicken.'}
            {item?.eFormat === 'zugferd' ? ' Die E-Rechnung steckt in der PDF.' : ''}
          </p>
          <div className="dlg-actions vt-actions">
            <button type="button" className="linkbtn" onClick={onAnsehen}>
              PDF ansehen
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Später
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
